import { User, GlobalConfig } from '../types';
import { hasUnlimitedTokens, getTokenMultiplier, calculateTokenRefresh } from './promoEffectsManager';

/**
 * Check if user can send a message based on token limits
 * UNIFIED TOKEN LIMITS - Must match constants.ts and RankTiersPanel defaults
 */
export const canSendMessage = (
    user: User | null,
    config: GlobalConfig,
    estimatedTokenCost: number
): { canSend: boolean; reason?: string } => {
    if (!user) {
        return { canSend: true }; // Guest users have no limits
    }

    // Check if user has unlimited tokens
    if (hasUnlimitedTokens(user)) {
        return { canSend: true };
    }

    // Get tier-specific limits from rankTiers if available, otherwise use config
    const userTier = user.isMax ? 'max' : user.isPremium ? 'premium' : 'standard';
    const tierConfig = config.rankTiers?.find(t => t.id === userTier);
    
    let tokenLimit: number;
    if (tierConfig) {
        tokenLimit = tierConfig.tokenLimit;
    } else {
        // Fallback to config values - MUST MATCH constants.ts DEFAULT_CONFIG (v2.0)
        if (user.isMax) {
            tokenLimit = config?.maxTokenLimit || 50000; // MAX tier: 50K
        } else if (user.isPremium) {
            tokenLimit = config?.premiumTokenLimit || 15000; // Premium tier: 15K
        } else {
            tokenLimit = config?.freeTokenLimit || 2000; // Standard tier: 2K
        }
    }
    
    // Handle unlimited (-1) token limit
    if (tokenLimit === -1) {
        return { canSend: true };
    }
    
    const currentUsage = user.dailyTokenUsage || 0;
    const projectedUsage = currentUsage + estimatedTokenCost;

    if (projectedUsage > tokenLimit) {
        return {
            canSend: false,
            reason: `Token limit exceeded. You have ${(tokenLimit - currentUsage).toLocaleString()} tokens remaining.`
        };
    }

    return { canSend: true };
};

/**
 * Calculate token cost with multiplier applied
 */
export const calculateTokenCost = (
    user: User | null,
    baseTokenCost: number
): number => {
    // If unlimited tokens, cost is still tracked but doesn't matter
    if (hasUnlimitedTokens(user)) {
        return baseTokenCost;
    }

    // Token cost is not affected by multiplier (multiplier only affects refresh)
    return baseTokenCost;
};

/**
 * Get effective token limit for display
 * UNIFIED TOKEN LIMITS - Must match constants.ts and RankTiersPanel defaults
 */
export const getEffectiveTokenLimit = (
    user: User | null,
    config: GlobalConfig
): number | 'unlimited' => {
    if (hasUnlimitedTokens(user)) {
        return 'unlimited';
    }

    // Get tier-specific limits from rankTiers if available
    const userTier = user?.isMax ? 'max' : user?.isPremium ? 'premium' : 'standard';
    const tierConfig = config.rankTiers?.find(t => t.id === userTier);
    
    if (tierConfig) {
        if (tierConfig.tokenLimit === -1) return 'unlimited';
        return tierConfig.tokenLimit;
    }

    // Fallback to config values - MUST MATCH constants.ts DEFAULT_CONFIG (v2.0)
    if (user?.isMax) {
        const maxLimit = config?.maxTokenLimit || 200000;
        return maxLimit === -1 ? 'unlimited' : maxLimit;
    } else if (user?.isPremium) {
        const premiumLimit = config?.premiumTokenLimit || 50000;
        return premiumLimit === -1 ? 'unlimited' : premiumLimit;
    } else {
        return config?.freeTokenLimit || 5000;
    }
};

/**
 * Get token refresh amount with multiplier
 * UNIFIED REFRESH RATES - Must match constants.ts and RankTiersPanel defaults
 */
export const getTokenRefreshAmount = (
    user: User | null,
    config: GlobalConfig
): number => {
    // Get tier-specific refresh rate from rankTiers if available
    const userTier = user?.isMax ? 'max' : user?.isPremium ? 'premium' : 'standard';
    const tierConfig = config.rankTiers?.find(t => t.id === userTier);
    
    let baseRefreshRate: number;
    if (tierConfig) {
        baseRefreshRate = tierConfig.tokenRefreshRate;
    } else {
        // Fallback to config values - MUST MATCH constants.ts DEFAULT_CONFIG (v2.0)
        if (user?.isMax) {
            baseRefreshRate = config?.maxRefreshRate || 20000; // MAX: 20K/hr
        } else if (user?.isPremium) {
            baseRefreshRate = config?.premiumRefreshRate || 5000; // Premium: 5K/hr
        } else {
            baseRefreshRate = config?.standardRefreshRate || 800; // Standard: 800/hr
        }
    }

    return calculateTokenRefresh(user, baseRefreshRate);
};

/**
 * Get token usage percentage
 */
export const getTokenUsagePercentage = (
    user: User | null,
    config: GlobalConfig
): number => {
    if (!user) return 0;
    
    if (hasUnlimitedTokens(user)) {
        return 0; // Always show 0% when unlimited
    }

    const limit = getEffectiveTokenLimit(user, config);
    if (limit === 'unlimited') return 0;

    const currentUsage = user.dailyTokenUsage || 0;
    return Math.min(100, (currentUsage / limit) * 100);
};

/**
 * Get display text for token status
 */
export const getTokenStatusText = (
    user: User | null,
    config: GlobalConfig
): string => {
    if (!user) return 'No limits for guests';
    
    if (hasUnlimitedTokens(user)) {
        return '∞ Unlimited Tokens Active';
    }

    const currentUsage = user.dailyTokenUsage || 0;
    const limit = getEffectiveTokenLimit(user, config);
    
    if (limit === 'unlimited') {
        return '∞ Unlimited';
    }

    const remaining = limit - currentUsage;
    const multiplier = getTokenMultiplier(user);

    if (multiplier > 1) {
        return `${remaining.toLocaleString()} / ${limit.toLocaleString()} (${multiplier}x boost)`;
    }

    return `${remaining.toLocaleString()} / ${limit.toLocaleString()}`;
};

/**
 * Check if user should see upgrade prompt
 */
export const shouldShowUpgradePrompt = (
    user: User | null,
    config: GlobalConfig
): boolean => {
    if (!user || user.isPremium) return false;
    
    // Don't show if user has unlimited tokens
    if (hasUnlimitedTokens(user)) return false;

    const usagePercent = getTokenUsagePercentage(user, config);
    return usagePercent > 80; // Show when over 80% usage
};
